import { c as _c } from "react/compiler-runtime";
import { ConversationUserActionFilterConfigColumn, GetInteractionAggregatesReqGroupBy, QueryOrdering, TopicFilterConfigColumn } from '@/api/generated/model';
import { AggregateBy } from '@/domains/dashboard/components/GlobalfiltersRow';
import { getBonusFilter } from '@/contexts/userIntelligence/UserIntelligenceProvider';
import { RowKind } from '@/domains/home/components/home-tabs/user-intelligence/_components';
import { AllFilterValue, CONVERSATION_CUSTOM_COLUMN_PREFIX, CUSTOM_COLUMN_PREFIX, FilterColumnValue, FILTERS, GROUPBY_VALUES, isConversationCustomColumn, isCustomAggregate, isCustomColumn, isTag, TAG_PREFIX } from '@/models/common/filters';
import { isFilterOnlyGroupByKind } from '@/utils/filter-utils';
import { GroupByValue } from '@/types/groupBy';
import { useMemo } from 'react';

// TODO: should make filterKind typesafe
export function mapFilterKindToAPIInteractionAggregatesGroupBy(filterKind: AllFilterValue | null, aggregateBy: AggregateBy): GetInteractionAggregatesReqGroupBy | undefined {
  if (!filterKind) return;
  // interaction_id is filter-only — there is no group_by equivalent on the backend.
  // Return undefined so the FilterPicker doesn't try to fetch a value list.
  if (filterKind === 'interaction_id') return undefined;
  if (filterKind === 'language') return {
    kind: GROUPBY_VALUES.language
  };
  if (filterKind === 'user_intent') return {
    kind: GROUPBY_VALUES.userIntent
  };
  if (filterKind === 'user_action') return {
    kind: GROUPBY_VALUES.userAction
  };
  if (filterKind === 'user_feedback_v2') return {
    kind: GROUPBY_VALUES.userFeedbackV2
  };
  if (filterKind === 'conversation_error_status') return {
    kind: GROUPBY_VALUES.conversationErrorStatus
  };
  if (filterKind === 'negative_user_intent_v2') return {
    kind: GROUPBY_VALUES.negativeUserIntentV2
  };
  if (isTag(filterKind)) return {
    kind: GROUPBY_VALUES.tag,
    tag: filterKind.replace(TAG_PREFIX, '')
  };
  if (isCustomColumn(filterKind)) return {
    kind: 'custom_column',
    custom_column: filterKind.replace(CUSTOM_COLUMN_PREFIX, '')
  };
  if (isConversationCustomColumn(filterKind)) return {
    kind: 'conversation_custom_column',
    custom_column: filterKind.replace(CONVERSATION_CUSTOM_COLUMN_PREFIX, '')
  };
  if (isCustomAggregate(filterKind)) return {
    kind: aggregateBy === 'interaction' ? 'custom_aggregate' : 'conversation_custom_aggregate',
    custom_aggregate: filterKind.split(':')[1]
  };
  if (isFilterOnlyGroupByKind(filterKind)) return undefined;
  return {
    kind: filterKind
  } as GetInteractionAggregatesReqGroupBy;
}
export function useMapGroupByValueToInteractionAggregatesAPIGroupBy(groupBy, deps) {
  const $ = _c(24);
  const {
    search,
    onlyCustom,
    sortBy,
    aggregateBy
  } = deps;
  let t0;
  bb0: {
    if (!groupBy) {
      t0 = undefined;
      break bb0;
    }
    if (groupBy === "user_action") {
      const t1 = aggregateBy === "conversation" ? GROUPBY_VALUES.conversationUserAction : GROUPBY_VALUES.userAction;
      const t2 = !search && sortBy.by !== "positive_feedback" && sortBy.by !== "negative_feedback";
      let t3;
      if ($[0] !== onlyCustom || $[1] !== t1 || $[2] !== t2) {
        t3 = {
          kind: t1,
          include_empty: t2,
          only_customs: onlyCustom
        };
        $[0] = onlyCustom;
        $[1] = t1;
        $[2] = t2;
        $[3] = t3;
      } else {
        t3 = $[3];
      }
      t0 = t3;
      break bb0;
    }
    if (groupBy === "keyword") {
      let t1;
      if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = {
          kind: GROUPBY_VALUES.keyword
        };
        $[4] = t1;
      } else {
        t1 = $[4];
      }
      t0 = t1;
      break bb0;
    }
    if (groupBy === "user_intent") {
      let t1;
      if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = {
          kind: GROUPBY_VALUES.userIntent
        };
        $[5] = t1;
      } else {
        t1 = $[5];
      }
      t0 = t1;
      break bb0;
    }
    if (groupBy === "topic") {
      const t1 = aggregateBy === "conversation" ? GROUPBY_VALUES.conversationTopic : GROUPBY_VALUES.topic;
      let t2;
      if ($[6] !== t1) {
        t2 = {
          kind: t1
        };
        $[6] = t1;
        $[7] = t2;
      } else {
        t2 = $[7];
      }
      t0 = t2;
      break bb0;
    }
    if (groupBy === "emotions") {
      let t1;
      if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = {
          kind: GROUPBY_VALUES.emotions
        };
        $[8] = t1;
      } else {
        t1 = $[8];
      }
      t0 = t1;
      break bb0;
    }
    if (groupBy === "negative_user_intent_v2") {
      let t1;
      if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = {
          kind: GROUPBY_VALUES.negativeUserIntentV2
        };
        $[9] = t1;
      } else {
        t1 = $[9];
      }
      t0 = t1;
      break bb0;
    }
    if (groupBy === "sentiment") {
      const t1 = aggregateBy === "conversation" ? GROUPBY_VALUES.conversationSentiment : GROUPBY_VALUES.sentiment;
      let t2;
      if ($[10] !== t1) {
        t2 = {
          kind: t1
        };
        $[10] = t1;
        $[11] = t2;
      } else {
        t2 = $[11];
      }
      t0 = t2;
      break bb0;
    }
    if (groupBy === "data_source") {
      let t1;
      if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = {
          kind: GROUPBY_VALUES.dataSource
        };
        $[12] = t1;
      } else {
        t1 = $[12];
      }
      t0 = t1;
      break bb0;
    }
    if (groupBy === "interaction_label") {
      let t1;
      if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = {
          kind: GROUPBY_VALUES.interactionLabel
        };
        $[13] = t1;
      } else {
        t1 = $[13];
      }
      t0 = t1;
      break bb0;
    }
    if (groupBy === "conversation_label") {
      let t1;
      if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = {
          kind: GROUPBY_VALUES.conversationLabel
        };
        $[14] = t1;
      } else {
        t1 = $[14];
      }
      t0 = t1;
      break bb0;
    }
    if (groupBy.startsWith(TAG_PREFIX)) {
      let t1;
      if ($[15] !== groupBy) {
        t1 = groupBy.split(":");
        $[15] = groupBy;
        $[16] = t1;
      } else {
        t1 = $[16];
      }
      let t2;
      if ($[17] !== t1[1]) {
        t2 = {
          kind: GROUPBY_VALUES.tag,
          tag: t1[1]
        };
        $[17] = t1[1];
        $[18] = t2;
      } else {
        t2 = $[18];
      }
      t0 = t2;
      break bb0;
    }
    if (isCustomAggregate(groupBy)) {
      const t1 = aggregateBy === "conversation" ? GROUPBY_VALUES.conversationCustomAggregate : GROUPBY_VALUES.customAggregate;
      let t2;
      if ($[19] !== groupBy) {
        t2 = groupBy.split(":");
        $[19] = groupBy;
        $[20] = t2;
      } else {
        t2 = $[20];
      }
      let t3;
      if ($[21] !== t1 || $[22] !== t2[1]) {
        t3 = {
          kind: t1,
          custom_aggregate: t2[1]
        };
        $[21] = t1;
        $[22] = t2[1];
        $[23] = t3;
      } else {
        t3 = $[23];
      }
      t0 = t3;
      break bb0;
    }
    t0 = undefined;
  }
  const value = t0;
  return value;
}
export function getBonusFilterKind(rowKind: RowKind | undefined, groupBy: GroupByValue | undefined) {
  if (rowKind === 'topic') return TopicFilterConfigColumn.topic;
  // if (rowKind === 'user_intent') return IntentFilterConfigColumn.intent;
  if (rowKind === 'user_action') return ConversationUserActionFilterConfigColumn.conversation_user_action;
  const bonusFilterKind = groupBy ? getBonusFilter(groupBy) : undefined;
  return bonusFilterKind;
}
export function mapGroupByValueToFilterColumnValue(groupByValue: GroupByValue): FilterColumnValue {
  if (groupByValue === 'user_action') return FILTERS.columnUserAction;
  if (groupByValue === 'user_intent') return FILTERS.columnIntent;
  if (groupByValue === 'negative_user_intent_v2') return FILTERS.columnNegativeIntentV2;
  if (groupByValue === 'topic') return FILTERS.columnTopic;
  if (groupByValue === 'emotions') return FILTERS.columnEmotions;
  if (groupByValue === 'sentiment') return FILTERS.columnSentiment;
  if (groupByValue === 'data_source') return FILTERS.columnDataSource;
  // if (groupByValue.startsWith('tag:')) return groupByValue;

  // this should be the tag: case
  return groupByValue as FilterColumnValue;
}